import { get, post, put, del, patch } from 'aws-amplify/api';
import { normalizeAmplifyError } from '../api/errors';
import { clearAppStorage } from './clearAppStorage';

type ApiBody = FormData | object | string | number | boolean | null;

type ApiInit = {
    body?: ApiBody;
    headers?: Record<string, string>;
    queryParams?: Record<string, string>;
};

type AmplifyOptions = NonNullable<Parameters<typeof get>[0]['options']>;

function amplifyOptions(init?: ApiInit): AmplifyOptions | undefined {
    return init as AmplifyOptions | undefined;
}

type RestOperation = {
    response: Promise<{
        statusCode?: number;
        body: { json: () => Promise<unknown> };
    }>;
};

function isEmptyJsonBodyError(error: unknown): boolean {
    return (
        error instanceof SyntaxError ||
        (error instanceof Error &&
            /unexpected end of json/i.test(error.message))
    );
}

async function parseResponseBody(
    statusCode: number | undefined,
    body: { json: () => Promise<unknown> }
): Promise<unknown> {
    if (statusCode === 204) {
        return undefined;
    }
    try {
        return await body.json();
    } catch (error) {
        if (isEmptyJsonBodyError(error)) {
            return undefined;
        }
        throw error;
    }
}

async function parseResponse<T>(operation: RestOperation): Promise<T> {
    try {
        const { body, statusCode } = await operation.response;
        const json = await parseResponseBody(statusCode, body);
        return json as T;
    } catch (error) {
        const apiError = await normalizeAmplifyError(error);
        if (apiError.isUnauthorized) {
            clearAppStorage();
            location.replace('/login');
        }
        throw apiError;
    }
}

export const apiClient = {
    get: <T>(apiName: string, path: string, init?: ApiInit): Promise<T> =>
        parseResponse<T>(get({ apiName, path, options: amplifyOptions(init) })),

    post: <T>(apiName: string, path: string, init?: ApiInit): Promise<T> =>
        parseResponse<T>(
            post({
                apiName,
                path,
                options: {
                    body: amplifyOptions(init)?.body,
                    headers: init?.headers
                }
            })
        ),

    put: <T>(apiName: string, path: string, init?: ApiInit): Promise<T> =>
        parseResponse<T>(
            put({
                apiName,
                path,
                options: {
                    body: amplifyOptions(init)?.body,
                    headers: init?.headers
                }
            })
        ),

    patch: <T>(apiName: string, path: string, init?: ApiInit): Promise<T> =>
        parseResponse<T>(
            patch({
                apiName,
                path,
                options: {
                    body: amplifyOptions(init)?.body,
                    headers: init?.headers
                }
            })
        ),

    del: <T>(apiName: string, path: string, init?: ApiInit): Promise<T> =>
        parseResponse<T>(del({ apiName, path, options: amplifyOptions(init) }))
};
